import React, { useState, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Loader2, Check, AlertTriangle, X, Sun, Moon } from 'lucide-react';
import { Switch } from './ui/switch';
import { API_URL } from '../config';
import { useAdvancedQAView } from '../context/AdvancedQAViewContext';
import { FONT, EASE, ACCENT, fmtNum } from '../pages/kyc-stats/components/format';
import UsageChart from './UsageChart';

// QA roles (any flavour) — mirrors AppSidebar's QA-access gate. These (+ admin,
// + API-granted graders) may see the QA Manager section.
const QA_ROLES = ['qa', 'qa-trainee', 'qa-admin', 'host-qa-admin', 'payments-qa', 'us-qa', 'kyc-qa', 'kick-qa', 'kpp-qa', 'kick-mod-qa', 'kps-qa', 'host-qa', 'admin'];
// QA grader roles that get the "Active Tickets" grading stat (admin/developer excluded).
const QA_GRADER_ROLES = ['qa', 'qa-trainee', 'qa-admin', 'host-qa-admin', 'payments-qa', 'us-qa', 'kyc-qa', 'kick-qa', 'kpp-qa', 'kick-mod-qa', 'kps-qa', 'host-qa'];
// TEMP preview override: these emails also see the regular-'user' ACP usage view
// on their own profile (so staff can preview how it looks). Remove when done.
const ACP_USAGE_PREVIEW_EMAILS = ['filipkozomara@mebit.io'];

// ── Nike primitives (monochrome ink/canvas, hairline, square corners) ──
const Eyebrow = ({ children, className = '' }) => (
  <div className={`text-[11px] uppercase tracking-[0.32em] font-bold text-[#707072] dark:text-white/40 ${className}`}>
    {children}
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="mb-4">
    <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#707072] dark:text-white/40">{children}</div>
    <div className="mt-3 h-px w-full bg-[#cacacb] dark:bg-white/[0.08]" />
  </div>
);

const ProfileModal = ({ isOpen, onClose }) => {
  const { user, setUser } = useAuth();
  const { theme, changeTheme } = useTheme();
  const { enabled: advancedQAEnabled, setEnabled: setAdvancedQAEnabled } = useAdvancedQAView();
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasQAAccess, setHasQAAccess] = useState(false);
  const [acpUsage, setAcpUsage] = useState(null); // { total, daily:[{day,count}] }
  const [qaStats, setQaStats] = useState(null);    // { activeTickets, gradedTickets }
  const [loadingInsight, setLoadingInsight] = useState(false);

  const role = user?.role;
  const isRegularUser = role === 'user'
    || ACP_USAGE_PREVIEW_EMAILS.includes((user?.email || '').toLowerCase());
  const isQaGrader = QA_GRADER_ROLES.includes(role);
  const hasInsight = isRegularUser || isQaGrader;

  // Keep username synced with the user object, but don't fire API calls here.
  useEffect(() => {
    if (user) {
      const emailPrefix = user.email?.split('@')[0] || '';
      setUsername(user.name || emailPrefix);
    }
  }, [user?._id, user?.name, user?.email]);

  // On open: resolve QA access (for the QA Manager toggle) + fetch the
  // role-appropriate insight (user → ACP usage, QA grader → active tickets).
  useEffect(() => {
    if (!isOpen || !user) return;

    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    // QA-access gate: QA roles + admin pass immediately; others via API.
    if (QA_ROLES.includes(role)) {
      setHasQAAccess(true);
    } else {
      axios.get(`${API_URL}/api/qa/check-access`, { headers })
        .then((res) => { if (!cancelled) setHasQAAccess(!!res.data?.hasAccess); })
        .catch(() => { if (!cancelled) setHasQAAccess(false); });
    }

    // Role-specific insight.
    if (isRegularUser) {
      setLoadingInsight(true);
      axios.get(`${API_URL}/api/qa/acp/my-usage`, { headers })
        .then((res) => { if (!cancelled) setAcpUsage(res.data); })
        .catch((err) => { if (!cancelled) console.error('Error fetching ACP usage:', err); })
        .finally(() => { if (!cancelled) setLoadingInsight(false); });
    } else if (isQaGrader) {
      setLoadingInsight(true);
      axios.get(`${API_URL}/api/qa/my-stats`, { headers })
        .then((res) => { if (!cancelled) setQaStats(res.data); })
        .catch((err) => { if (!cancelled) console.error('Error fetching QA stats:', err); })
        .finally(() => { if (!cancelled) setLoadingInsight(false); });
    }

    return () => { cancelled = true; };
  }, [isOpen, user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc closes the modal — capture phase guarantees this runs first.
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape, { capture: true });
    return () => window.removeEventListener('keydown', handleEscape, { capture: true });
  }, [isOpen, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const handleUpdateProfile = useCallback(async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/auth/profile`,
        { name: username },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser(response.data);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  }, [username, setUser]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const inputCls =
    'w-full px-3.5 py-2.5 text-[13px] rounded-sm bg-transparent border border-[#cacacb] dark:border-white/10 text-[#111111] dark:text-white placeholder-[#9e9ea0] dark:placeholder-white/30 focus:outline-none focus:border-[#111111] dark:focus:border-white/40 transition-colors';
  const labelCls = 'block text-[10px] font-bold uppercase tracking-[0.18em] text-[#707072] dark:text-white/40 mb-2';

  // Landscape sizing: wide enough that the user's usage graph + settings fit
  // side-by-side with no scroll. Narrower when there's no insight to show.
  const maxW = isRegularUser ? 'max-w-5xl' : isQaGrader ? 'max-w-3xl' : 'max-w-2xl';
  const gridCls = hasInsight ? `grid grid-cols-1 ${isRegularUser ? 'lg:grid-cols-5' : 'lg:grid-cols-2'} gap-8 lg:gap-10` : '';
  const settingsSpan = !hasInsight ? '' : isRegularUser ? 'lg:col-span-2' : 'lg:col-span-1';
  const insightSpan = isRegularUser ? 'lg:col-span-3' : 'lg:col-span-1';

  const settings = (
    <div className={`${settingsSpan} space-y-9`}>
      {/* Identity */}
      <section>
        <SectionLabel>Identity</SectionLabel>
        <div className="space-y-4">
          <div>
            <label htmlFor="username" className={labelCls}>Username</label>
            <input id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="email" className={labelCls}>Email</label>
            <input id="email" type="email" value={user?.email || ''} disabled className={`${inputCls} bg-[#f5f5f5] dark:bg-white/[0.04] cursor-not-allowed text-[#707072] dark:text-white/40`} />
            <p className="mt-2 text-[11px] text-[#9e9ea0] dark:text-white/30">Locked · cannot be changed</p>
          </div>
          <button
            onClick={handleUpdateProfile}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#111111] dark:bg-white text-white dark:text-[#111111] rounded-sm text-[11px] font-bold uppercase tracking-[0.18em] hover:opacity-80 transition-opacity disabled:opacity-40"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Saving' : 'Save Changes'}
          </button>
        </div>
      </section>

      {/* Appearance */}
      <section>
        <SectionLabel>Appearance</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: 'white', label: 'Light', Icon: Sun, swatch: '#ffffff' },
            { value: 'dark', label: 'Dark', Icon: Moon, swatch: '#0A0A0B' },
          ].map((opt) => {
            const selected = theme === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => changeTheme(opt.value)}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-sm border transition-colors text-left ${
                  selected
                    ? 'border-[#111111] dark:border-white bg-[#f5f5f5] dark:bg-white/[0.06]'
                    : 'border-[#cacacb] dark:border-white/10 hover:border-[#111111] dark:hover:border-white/40'
                }`}
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-sm border border-[#cacacb] dark:border-white/15" style={{ backgroundColor: opt.swatch }}>
                  <opt.Icon className="w-3.5 h-3.5" style={{ color: opt.value === 'white' ? '#111111' : '#ffffff' }} />
                </span>
                <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#111111] dark:text-white">{opt.label}</span>
                {selected && <Check className="w-4 h-4 ml-auto text-[#111111] dark:text-white" />}
              </button>
            );
          })}
        </div>
      </section>

      {/* QA Manager — only visible to QA roles / QA-access users. */}
      {hasQAAccess && (
        <section>
          <SectionLabel>QA Manager</SectionLabel>
          <div className="flex items-start justify-between gap-4 border border-[#e5e5e5] dark:border-white/[0.06] rounded-sm px-4 py-3.5">
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-[#111111] dark:text-white">Advanced QA View</p>
              <p className="text-[12px] text-[#707072] dark:text-white/40 mt-1 leading-snug">
                New layout for ticket modals — context rail, command bar, slide-over ACP. Switches active immediately, including for any open ticket.
              </p>
            </div>
            <Switch checked={advancedQAEnabled} onCheckedChange={setAdvancedQAEnabled} aria-label="Toggle Advanced QA View" />
          </div>
        </section>
      )}
    </div>
  );

  const insight = hasInsight && (
    <div className={`${insightSpan} space-y-5`}>
      {isRegularUser ? (
        <section>
          <SectionLabel>Tools Usage · Last 30 Days</SectionLabel>
          <div className="flex items-baseline gap-3 mb-5">
            <span className="text-[clamp(2.5rem,5vw,4rem)] font-black leading-none tabular-nums text-[#111111] dark:text-white">
              {loadingInsight ? '·' : fmtNum(acpUsage?.total ?? 0)}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9e9ea0] dark:text-white/30">Tool calls</span>
          </div>
          <UsageChart data={acpUsage?.daily || []} height={210} />
          <p className="mt-3 text-[11px] text-[#9e9ea0] dark:text-white/30">
            Tool calls you made across Clara's tools over the last 30 days.
          </p>
        </section>
      ) : (
        <section>
          <SectionLabel>Grading · Your Tickets</SectionLabel>
          <div className="grid grid-cols-2 gap-px bg-[#e5e5e5] dark:bg-white/[0.06] border border-[#e5e5e5] dark:border-white/[0.06]">
            <div className="bg-white dark:bg-[#0A0A0B] p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#707072] dark:text-white/40">Active Tickets</div>
              <div className="mt-2 text-4xl font-black tabular-nums" style={{ color: ACCENT.amber }}>
                {loadingInsight ? '·' : fmtNum(qaStats?.activeTickets ?? 0)}
              </div>
            </div>
            <div className="bg-white dark:bg-[#0A0A0B] p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#707072] dark:text-white/40">Graded · Total</div>
              <div className="mt-2 text-4xl font-black tabular-nums text-[#111111] dark:text-white">
                {loadingInsight ? '·' : fmtNum(qaStats?.gradedTickets ?? 0)}
              </div>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-[#9e9ea0] dark:text-white/30">
            Active = tickets you're still working (selected, draft, awaiting input).
          </p>
        </section>
      )}
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 sm:p-6"
      style={{ fontFamily: FONT }}
      onClick={handleBackdropClick}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.22, ease: EASE }}
        className={`relative bg-white dark:bg-[#0A0A0B] border border-[#cacacb] dark:border-white/10 rounded-sm shadow-2xl w-full ${maxW} max-h-[88vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-7 pt-7 pb-5 bg-white/90 dark:bg-[#0A0A0B]/90 backdrop-blur border-b border-[#e5e5e5] dark:border-white/[0.06]">
          <div>
            <Eyebrow className="mb-2">Account · Preferences</Eyebrow>
            <h2 className="text-3xl font-black uppercase tracking-[-0.02em] text-[#111111] dark:text-white leading-none">Profile</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-1 rounded-sm border border-[#cacacb] dark:border-white/10 text-[#111111] dark:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-7 py-7 space-y-9">
          {/* Banners */}
          {error && (
            <div className="flex items-start gap-2 border rounded-sm px-4 py-3" style={{ borderColor: `${ACCENT.red}55` }}>
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT.red }} />
              <p className="text-[12px] flex-1" style={{ color: ACCENT.red }}>{error}</p>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 border rounded-sm px-4 py-3" style={{ borderColor: `${ACCENT.green}55` }}>
              <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT.green }} />
              <p className="text-[12px] flex-1" style={{ color: ACCENT.green }}>{success}</p>
            </div>
          )}

          {/* Body — settings + role-specific insight, side by side (landscape). */}
          <div className={gridCls}>
            {settings}
            {insight}
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default memo(ProfileModal);
